/**
 * LoadingState Component
 * 
 * Reusable loading state with spinner and optional message.
 */

import { useTranslation } from '@/core';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingStateProps {
  message?: string;
  className?: string;
  compact?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

export function LoadingState({
  message,
  className,
  compact = false,
  size = 'md',
}: LoadingStateProps) {
  const { t } = useTranslation();
  
  const displayMessage = message ?? t('common.loading');

  if (compact) {
    return (
      <div className={cn('flex items-center gap-2 text-muted-foreground', className)}>
        <Loader2 className={cn('animate-spin', sizeClasses.sm)} />
        <span className="text-sm">{displayMessage}</span>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col items-center justify-center py-12', className)}>
      <Loader2 className={cn('animate-spin text-primary mb-4', sizeClasses[size])} />
      <p className="text-sm text-muted-foreground">{displayMessage}</p>
    </div>
  );
}

export default LoadingState;
